package br.com.duoma.duomaproducao.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import br.com.duoma.duomaproducao.ui.misc.mediaQuery

// ─── Data model ──────────────────────────────────────────────────────────────

data class HeatmapCell<C, R>(
    val row: R,
    val col: C,
    val value: Float,
)

// ─── Public composable ────────────────────────────────────────────────────────

@Composable
fun <C, R> HeatmapChart(
    cells: List<HeatmapCell<C, R>>,
    modifier: Modifier = Modifier,
    title: String? = null,
    colorLow: Color = Color.Unspecified,
    colorHigh: Color = Color.Unspecified,
    cellHeight: Dp = mediaQuery(48.dp, 36.dp),
    cellSpacing: Dp = 5.dp,
    showValues: Boolean = true,
    valueFormat: (Float) -> String = { it.toInt().toString() },
    colLabels: List<Pair<String, C>> = emptyList(),
    rowLabels: List<Pair<String, R>> = emptyList(),
    yLabelWidth: Dp = 88.dp,
    showLegend: Boolean = true,
) {
    if (cells.isEmpty()) return

    val resolvedColorLow  = if (colorLow  == Color.Unspecified) MaterialTheme.colorScheme.surfaceVariant else colorLow
    val resolvedColorHigh = if (colorHigh == Color.Unspecified) MaterialTheme.colorScheme.primary        else colorHigh

    val valueMap = remember(cells) { cells.associate { (it.row to it.col) to it.value } }
    val minVal   = remember(cells) { cells.minOf { it.value } }
    val maxVal   = remember(cells) { cells.maxOf { it.value } }

    Column(modifier = modifier) {

        if (title != null) {
            SectionHeader(title = title)
            Spacer(Modifier.height(16.dp))
        }

        Card(
            modifier = Modifier.fillMaxWidth(),
            elevation = CardDefaults.elevatedCardElevation(defaultElevation = 2.dp),
            colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
            shape = RoundedCornerShape(12.dp),
        ) {
            Column(Modifier.padding(horizontal = 16.dp, vertical = 12.dp)) {

                BoxWithConstraints(Modifier.fillMaxWidth()) {
                    val colCount   = colLabels.size.coerceAtLeast(1)
                    // Each column gets at least 48dp so labels are always readable.
                    // On wide screens this naturally expands to fill the available space.
                    val cellWidth  = maxOf(36.dp, (maxWidth - yLabelWidth) / colCount)
                    val scrollState = rememberScrollState()

                    Row(Modifier.fillMaxWidth()) {

                        // ── Sticky left: row labels ──────────────────────────
                        Column(Modifier.width(yLabelWidth)) {
                            // Blank slot to align with the column-header row
                            Spacer(Modifier.height(40.dp + cellSpacing))
                            rowLabels.forEach { (labelR, _) ->
                                Box(
                                    contentAlignment = Alignment.CenterEnd,
                                    modifier = Modifier
                                        .width(yLabelWidth)
                                        .height(cellHeight)
                                        .padding(end = 8.dp),
                                ) {
                                    Text(
                                        text = labelR,
                                        style = MaterialTheme.typography.bodySmall,
                                        fontWeight = FontWeight.Medium,
                                        textAlign = TextAlign.End,
                                        color = MaterialTheme.colorScheme.onSurface,
                                        maxLines = 2,
                                    )
                                }
                                Spacer(Modifier.height(cellSpacing))
                            }
                        }

                        // ── Scrollable right: col headers + data cells ───────
                        Column(Modifier.horizontalScroll(scrollState)) {

                            // Column-header row
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                colLabels.forEach { (labelC, _) ->
                                    Box(
                                        contentAlignment = Alignment.Center,
                                        modifier = Modifier
                                            .width(cellWidth)
                                            .height(40.dp)
                                            .padding(cellSpacing / 2),
                                    ) {
                                        Text(
                                            text = labelC,
                                            style = MaterialTheme.typography.labelMedium,
                                            fontWeight = FontWeight.Medium,
                                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                                            textAlign = TextAlign.Center,
                                            maxLines = 2,
                                        )
                                    }
                                }
                            }

                            Spacer(Modifier.height(cellSpacing))

                            // Data rows (cells only — label is in the sticky column)
                            rowLabels.forEach { (_, row) ->
                                Row(verticalAlignment = Alignment.CenterVertically) {
                                    colLabels.forEach { (_, col) ->
                                        HeatmapCell(
                                            value = valueMap[row to col],
                                            minVal = minVal,
                                            maxVal = maxVal,
                                            cellHeight = cellHeight,
                                            cellSpacing = cellSpacing,
                                            colorLow = resolvedColorLow,
                                            colorHigh = resolvedColorHigh,
                                            showValue = showValues,
                                            valueFormat = valueFormat,
                                            onSurfaceColor = MaterialTheme.colorScheme.onSurface,
                                            modifier = Modifier.width(cellWidth),
                                        )
                                    }
                                }
                                Spacer(Modifier.height(cellSpacing))
                            }
                        }
                    }
                }

                // ── Legend ───────────────────────────────────────────────────
                if (showLegend) {
                    Spacer(Modifier.height(16.dp))
                    HeatmapLegend(
                        colorLow = resolvedColorLow,
                        colorHigh = resolvedColorHigh,
                        minVal = minVal,
                        maxVal = maxVal,
                        valueFormat = valueFormat,
                        subtextColor = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.align(Alignment.End),
                    )
                }
            }
        }
    }
}

// ─── Private helpers ─────────────────────────────────────────────────────────

@Composable
private fun HeatmapCell(
    value: Float?,
    minVal: Float,
    maxVal: Float,
    cellHeight: Dp,
    cellSpacing: Dp,
    colorLow: Color,
    colorHigh: Color,
    showValue: Boolean,
    valueFormat: (Float) -> String,
    onSurfaceColor: Color,
    modifier: Modifier = Modifier,
) {
    val fraction = if (value != null && maxVal > minVal) {
        ((value - minVal) / (maxVal - minVal)).coerceIn(0f, 1f)
    } else {
        0f
    }

    val cellColor = if (value != null) lerpColor(
        colorLow,
        colorHigh,
        fraction
    ) else Color(0xFFF0F0F0)
    val textColor = if (fraction > 0.55f) Color.White else onSurfaceColor

    Box(
        contentAlignment = Alignment.Center,
        modifier = modifier
            .padding(horizontal = cellSpacing / 2)
            .height(cellHeight)
            .clip(RoundedCornerShape(4.dp))
            .background(cellColor),
    ) {
        if (showValue && value != null) {
            Text(
                text = valueFormat(value),
                style = MaterialTheme.typography.labelSmall,
                fontWeight = FontWeight.SemiBold,
                color = textColor,
                textAlign = TextAlign.Center,
            )
        }
    }
}

@Composable
private fun HeatmapLegend(
    colorLow: Color,
    colorHigh: Color,
    minVal: Float,
    maxVal: Float,
    valueFormat: (Float) -> String,
    subtextColor: Color,
    modifier: Modifier = Modifier,
    steps: Int = 5,
) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = modifier,
    ) {
        Text(
            text = valueFormat(minVal),
            style = MaterialTheme.typography.labelSmall,
            color = subtextColor,
        )
        Spacer(Modifier.width(6.dp))
        Row {
            repeat(steps) { i ->
                val fraction = i / (steps - 1).toFloat()
                Box(
                    modifier = Modifier
                        .width(20.dp)
                        .height(12.dp)
                        .background(
                            lerpColor(
                                colorLow,
                                colorHigh,
                                fraction
                            )
                        )
                )
            }
        }
        Spacer(Modifier.width(6.dp))
        Text(
            text = valueFormat(maxVal),
            style = MaterialTheme.typography.labelSmall,
            color = subtextColor,
        )
    }
}

private fun lerpColor(a: Color, b: Color, fraction: Float): Color {
    val f = fraction.coerceIn(0f, 1f)
    return Color(
        red   = a.red   + (b.red   - a.red)   * f,
        green = a.green + (b.green - a.green) * f,
        blue  = a.blue  + (b.blue  - a.blue)  * f,
        alpha = a.alpha + (b.alpha - a.alpha)  * f,
    )
}
